import { Sparkles, MessageSquarePlus, History, ShieldCheck } from "lucide-react";

export const SUGGESTED = [
  "What are the strongest short-term opportunities right now?",
  "Why is Bitcoin showing WAIT?",
  "Compare Ethereum and Solana.",
  "What could cause this prediction to fail?",
  "What is the safest way to paper-test this opportunity?",
  "Explain RSI like I'm new to crypto",
  "Walk me through moving averages and when to use them",
  "What are the risks of trading altcoins right now?",
];

export const DEPTHS = ["beginner", "intermediate", "professional"];

export const DepthSwitch = ({ depth, onPick, compact }) => (
  <div className="flex items-center gap-1 rounded-full border border-slate-800 bg-slate-900/70 p-1" data-testid="chat-depth" role="radiogroup" aria-label="Answer depth">
    {DEPTHS.map((d) => (
      <button key={d} type="button" role="radio" aria-checked={depth === d} data-testid={`chat-depth-${d}`} onClick={() => onPick(d)}
        className={`rounded-full ${compact ? "px-2.5 py-1 text-[11px]" : "px-3.5 py-1.5 text-xs md:text-sm"} font-medium transition-colors ${depth === d ? "bg-cyan-500/20 text-cyan-100 border border-cyan-700/60" : "text-slate-400 hover:text-slate-100 border border-transparent"}`}>
        {d === "professional" ? "Pro" : d[0].toUpperCase() + d.slice(1)}
      </button>
    ))}
  </div>
);

const ago = (iso) => {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 60) return `${Math.max(1, m)}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

export const SuggestionButtons = ({ onPick, busy, layout = "grid" }) => (
  <div className={layout === "grid" ? "grid gap-2.5" : "flex gap-2.5 overflow-x-auto pb-1 -mx-1 px-1 snap-x"} data-testid="chat-suggestions">
    {SUGGESTED.map((q, i) => (
      <button key={q} type="button" data-testid={`suggestion-${i}`} disabled={busy} onClick={() => onPick(q)}
        className={`${layout === "grid" ? "w-full" : "shrink-0 snap-start max-w-[260px]"} text-left rounded-xl border border-slate-800 bg-slate-900/60 hover:border-cyan-700/70 hover:bg-cyan-500/[0.06] text-slate-200 px-3.5 py-3 text-sm leading-snug flex items-start gap-2.5 transition-colors disabled:opacity-50`}>
        <Sparkles className="w-4 h-4 text-cyan-300 shrink-0 mt-0.5" /> <span>{q}</span>
      </button>
    ))}
  </div>
);

export const ChatRail = ({ depth, onDepth, onSuggest, busy, sessions, activeSession, onOpenSession, onNewChat }) => (
  <aside className="hidden lg:flex flex-col gap-5 w-[320px] xl:w-[360px] shrink-0 overflow-y-auto pr-1" data-testid="chat-rail">
    <button type="button" onClick={onNewChat} data-testid="chat-new" className="btn-ghost rounded-xl px-4 py-3 text-sm font-medium flex items-center gap-2 justify-center border border-slate-700">
      <MessageSquarePlus className="w-4 h-4" /> New conversation
    </button>

    <div className="apx-card p-4 space-y-3">
      <div className="text-[11px] font-mono-ticker uppercase tracking-widest text-slate-500">Answer depth</div>
      <DepthSwitch depth={depth} onPick={onDepth} />
      <p className="text-xs text-slate-400 leading-relaxed">
        {depth === "beginner" && "Everyday language, every term defined, one idea per paragraph, with an example."}
        {depth === "intermediate" && "Assumes basic charting; includes the key numbers with one-line meanings."}
        {depth === "professional" && "Full technical & statistical evidence, data timestamps and what is unavailable."}
      </p>
    </div>

    <div className="apx-card p-4 space-y-3">
      <div className="text-[11px] font-mono-ticker uppercase tracking-widest text-slate-500">Suggested questions</div>
      <SuggestionButtons onPick={onSuggest} busy={busy} />
    </div>

    {sessions?.length > 0 && (
      <div className="apx-card p-4 space-y-2" data-testid="chat-sessions">
        <div className="text-[11px] font-mono-ticker uppercase tracking-widest text-slate-500 flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> Recent conversations</div>
        {sessions.slice(0, 8).map((s) => (
          <button key={s.session_id} type="button" data-testid="chat-session" onClick={() => onOpenSession(s.session_id)}
            className={`w-full text-left rounded-lg px-3 py-2.5 text-sm border transition-colors ${s.session_id === activeSession ? "border-cyan-700/60 bg-cyan-500/[0.08] text-cyan-50" : "border-slate-800/80 hover:border-slate-700 text-slate-300"}`}>
            <div className="truncate">{s.title || s.last_message}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">{s.count} messages · {ago(s.last_at)}</div>
          </button>
        ))}
      </div>
    )}

    <div className="rounded-xl border border-emerald-900/50 bg-emerald-500/[0.05] p-3.5 text-xs text-emerald-100/80 leading-relaxed flex gap-2" data-testid="chat-grounding-note">
      <ShieldCheck className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" />
      <span>Grounded answers: prices, indicators and timestamps come from the live EV-IDEX Opportunity Scanner and Signal Reports. The assistant says when data is unavailable instead of guessing. Not financial advice.</span>
    </div>
  </aside>
);
